import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BG_URL =
  "https://images.unsplash.com/photo-1549946463-0c1ba6fb541f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxzaGlweWFyZCUyMG5pZ2h0fGVufDB8fHx8MTc3OTY3OTE5OXww&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    company: "",
    phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [pendingMsg, setPendingMsg] = useState("");

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    setError("");
    const res = await register(form);
    setSubmitting(false);
    if (res.ok && res.pending) {
      setPendingMsg(res.message || "Thanks — your registration has been submitted for approval.");
    } else if (res.ok) {
      toast.success("Welcome — your account is ready");
      navigate("/");
    } else {
      setError(res.error);
    }
  };

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#F6F7F8] overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: `url(${BG_URL})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-black/70" aria-hidden />
      <div className="absolute inset-0 sos-grid-bg opacity-30" aria-hidden />

      <div className="relative z-10 w-full max-w-md px-6 py-10">
        <div className="mb-6 flex flex-col items-center text-center gap-4">
          <Logo className="h-14 w-auto" showWordmark={false} />
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#011836]">
              Create an account
            </h1>
            <p className="mt-2 text-xs uppercase tracking-[0.3em] text-[#5B6B7C]">
              Browse our vessel catalogue
            </p>
          </div>
        </div>

        {pendingMsg ? (
          <div className="sos-surface rounded-lg p-8 text-center space-y-4" data-testid="register-pending-card">
            <div className="mx-auto w-12 h-12 rounded-full bg-[#E6F2FA] flex items-center justify-center">
              <svg className="h-6 w-6 text-[#0088CC]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l2.5 2.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="font-display text-xl font-bold text-[#011836]">Registration submitted</h2>
            <p className="text-sm text-[#5B6B7C] leading-relaxed" data-testid="register-pending-message">{pendingMsg}</p>
            <Link to="/login" className="inline-block text-sm text-[#0088CC] hover:underline" data-testid="register-pending-login-link">
              Back to sign in
            </Link>
          </div>
        ) : (
        <form
          onSubmit={onSubmit}
          className="sos-surface rounded-lg p-6 space-y-4"
          data-testid="register-form"
        >
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Full name</Label>
            <Input
              required
              autoComplete="name"
              value={form.name}
              onChange={setField("name")}
              className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              data-testid="register-name-input"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Company</Label>
              <Input
                value={form.company}
                onChange={setField("company")}
                className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                data-testid="register-company-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Phone</Label>
              <Input
                type="tel"
                autoComplete="tel"
                value={form.phone}
                onChange={setField("phone")}
                className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                data-testid="register-phone-input"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Email</Label>
            <Input
              type="email"
              required
              autoComplete="email"
              value={form.email}
              onChange={setField("email")}
              className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              data-testid="register-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">
              Password
              <span className="ml-2 normal-case tracking-normal text-[10px] text-[#64748B]">
                (min 8 characters)
              </span>
            </Label>
            <Input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={form.password}
              onChange={setField("password")}
              className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
              data-testid="register-password-input"
            />
          </div>

          {error && (
            <div
              className="text-sm text-[#EF4444] border border-[#EF4444]/30 bg-[#FDECEC]/40 px-3 py-2 rounded"
              data-testid="register-error"
            >
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#0088CC] hover:bg-[#006DA6] text-white font-display tracking-wide"
            data-testid="register-submit-btn"
          >
            {submitting ? "Creating account…" : "Create account"}
          </Button>
          <p className="text-center text-[11px] text-[#94A3B8]">
            New accounts are reviewed by our team before activation.
          </p>
          <p className="text-center text-xs text-[#5B6B7C]">
            Already have an account?{" "}
            <Link to="/login" className="text-[#0088CC] hover:underline" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
